import React, { useState } from "react";
import { IconButton, Typography } from "@mui/joy";
import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import {
    CsvRounded,
    DownloadRounded,
    SelectAllRounded,
    TableViewRounded,
    VisibilityRounded,
} from "@mui-symbols-material/w300";
import { useTranslation } from "react-i18next";
import OptionsMenu, { OptionsMenuOption } from "../OptionsMenu";
import { ExportMode } from "../../utils/tableColumnLayout";
import { ExportFormat } from "../../utils/listExport";
import { getExportFormat, setExportFormat } from "../../utils/exportFormatStorage";

interface Props {
    onExport: (mode: ExportMode, format: ExportFormat) => void;
    disabled?: boolean;
    testId?: string;
    // Without column management (e.g. the asset's event history) there is no
    // "visible columns" notion, so the menu only offers the file formats
    withModeMenu?: boolean;
}

// Download button of the list headers: file format (Excel or CSV, remembered)
// plus the choice between the visible columns and all data
const ExportMenuButton = ({ onExport, disabled, testId, withModeMenu = true }: Props) => {
    const { t } = useTranslation();
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [format, setFormat] = useState<ExportFormat>(getExportFormat);

    const changeFormat = (next: ExportFormat) => {
        setFormat(next);
        setExportFormat(next);
    };

    const exportAs = (next: ExportFormat) => {
        changeFormat(next);
        onExport('all', next);
    };

    const options: OptionsMenuOption[] = withModeMenu
        ? [
            {
                icon: <VisibilityRounded />,
                title: t('Visible columns'),
                subtitle: t('Export only the columns currently shown in the table'),
                onClick: () => onExport('visible', format),
            },
            {
                icon: <SelectAllRounded />,
                title: t('All data'),
                subtitle: t('Export all columns, including hidden ones'),
                onClick: () => onExport('all', format),
            },
        ]
        : [
            {
                icon: <TableViewRounded />,
                title: 'XLSX',
                onClick: () => exportAs('xlsx'),
            },
            {
                icon: <CsvRounded />,
                title: 'CSV',
                onClick: () => exportAs('csv'),
            },
        ];

    return (
        <>
            <Tooltip title={t("Export")}>
                {/* span keeps the tooltip working while the button is disabled */}
                <span style={{ display: "inline-flex", marginLeft: "5px", flexShrink: 0 }}>
                    <IconButton
                        variant="outlined"
                        color="neutral"
                        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
                        disabled={disabled}
                        data-testid={testId}
                    >
                        <DownloadRounded />
                    </IconButton>
                </span>
            </Tooltip>
            <OptionsMenu
                anchorEl={menuAnchorEl}
                open={!!menuAnchorEl}
                onClose={() => setMenuAnchorEl(null)}
                options={options}
                header={withModeMenu && (
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                        <Typography level="body-sm" sx={{ color: "text.secondary" }}>{t("Format")}</Typography>
                        {/* Same compact switch as the list/tile view toggle of the file lists */}
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={format}
                            onChange={(_, next: ExportFormat | null) => {
                                // Clicking the selected button again yields null — keep the format
                                if (next) changeFormat(next);
                            }}
                            aria-label={t("Format")}
                            sx={{ flexShrink: 0, height: "32px", "& .MuiToggleButton-root": { px: "12px", py: 0 } }}
                        >
                            <ToggleButton value="xlsx" data-testid="export-format-xlsx">XLSX</ToggleButton>
                            <ToggleButton value="csv" data-testid="export-format-csv">CSV</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}
            />
        </>
    );
};

export default ExportMenuButton;
